// @file Global content picker session store.
// Tracks concurrent content picker sessions and which sources each session enables.

import { ALL_AV_SOURCES, ContentPickerSource } from '@@/bits/content_picker'
import type { ContentPickerSession, PickerConfigMap, PickerSourceState } from '@@/bits/content_picker_configs'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useGlobalContentPickerStore = defineStore('globalContentPickerStore', () => {
  const sessions = ref<Record<string, ContentPickerSession>>({})
  const currentSessionId = ref<string | null>(null)

  const currentSession = computed<ContentPickerSession | undefined>(() => {
    if (currentSessionId.value == null) return undefined
    return sessions.value[currentSessionId.value]
  })

  const currentPickerConfig = computed(() => {
    const session = currentSession.value
    if (session == null) return undefined
    const source = session.activeComponent
    if (source == null) return undefined
    return session.pickerConfigs[source]
  })

  // At most one screen recording session can exist at a time.
  // This returns that session if it exists.
  const globalScreenRecordingSession = computed<ContentPickerSession | undefined>(() => {
    return Object.values(sessions.value).find(
      (session) => session.activeComponent === ContentPickerSource.ScreenshareRecorder,
    )
  })

  const isAnyAvSessionActive = computed<boolean>(() => {
    return Object.values(sessions.value).some(
      (session) => session.activeComponent != null && ALL_AV_SOURCES.includes(session.activeComponent),
    )
  })

  function endSession(sessionId: string): void {
    delete sessions.value[sessionId]
    if (currentSessionId.value === sessionId) {
      const keys = Object.keys(sessions.value)
      currentSessionId.value = keys.length > 0 ? keys[keys.length - 1] : null
    }
  }

  function endCurrentSession(): void {
    if (currentSessionId.value != null) {
      endSession(currentSessionId.value)
    }
  }

  /**
   * Always create a fresh session with a random ID. Old sessions persist until explicitly ended.
   */
  function startSession(
    activeComponent: ContentPickerSource,
    config?: { pickerConfig?: PickerConfigMap; enabledSources?: ContentPickerSource[] },
  ): void {
    // It's technically possible to have say a screen recording and video recording simultaneously.
    // For simplicity, we don't. We'd have to resolve edge cases like how to handle an ongoing
    // video recording when a screen recording hits its time limit and stops.
    if (ALL_AV_SOURCES.includes(activeComponent) && isAnyAvSessionActive.value) {
      useGlobalSnackbarStore().setSnackbar({
        message: __('Please complete the current recording before starting a new one.'),
      })
      return
    }

    const id = uniqueId('cp-session-')
    sessions.value[id] = {
      id,
      enabledSources: config?.enabledSources ?? [...ALL_AV_SOURCES],
      activeComponent,
      pickerConfigs: config?.pickerConfig ?? {},
      pickerStates: {},
    }
    currentSessionId.value = id
  }

  function setCurrentSessionActiveComponent(source: ContentPickerSource): void {
    if (currentSessionId.value == null) return
    const session = sessions.value[currentSessionId.value]
    if (session == null) return
    session.activeComponent = source
  }

  function setPickerState(source: ContentPickerSource, state: PickerSourceState | undefined): void {
    if (currentSessionId.value == null) return
    const session = sessions.value[currentSessionId.value]
    if (session == null) return
    if (state == null) {
      delete session.pickerStates[source]
    } else {
      session.pickerStates[source] = state
    }
  }

  function setPickerStateForSession(
    sessionId: string,
    source: ContentPickerSource,
    state: PickerSourceState | undefined,
  ): void {
    const session = sessions.value[sessionId]
    if (session == null) return
    if (state == null) {
      delete session.pickerStates[source]
    } else {
      session.pickerStates[source] = state
    }
  }

  function switchToSessionId(id: string): void {
    currentSessionId.value = id
  }

  return {
    sessions,
    currentSessionId,
    currentSession,
    currentPickerConfig,
    globalScreenRecordingSession,
    endSession,
    endCurrentSession,
    startSession,
    setCurrentSessionActiveComponent,
    setPickerState,
    setPickerStateForSession,
    switchToSessionId,
  }
})

type GlobalContentPickerSessionConfig = { pickerConfig?: PickerConfigMap; enabledSources?: ContentPickerSource[] }

export { useGlobalContentPickerStore }
export type { GlobalContentPickerSessionConfig }
